import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { deleteAccount, fetchDeletionPreview } from "../../utils/utils";
import {
  getProviderLabel,
  getProviderType,
  reauthenticateUser,
  startMfaReauthChallenge,
  completeMfaReauth,
} from "../../hooks/useReauthentication";

const SUMMARY_ICONS = {
  profile: AccountCircleOutlinedIcon,
  posts: ArticleOutlinedIcon,
  comments: CommentOutlinedIcon,
  rsvps: EventAvailableOutlinedIcon,
  media: ImageOutlinedIcon,
  credentials: VpnKeyOutlinedIcon,
  active_campaigns: CampaignOutlinedIcon,
  organized_events_note: EventBusyOutlinedIcon,
};

const SUMMARY_LABELS = {
  profile: "Your profile and account information",
  posts: "Your posts and content",
  comments: "Your comments and interactions",
  rsvps: "Your event RSVPs and check-ins",
  media: "Profile images and uploaded media",
  credentials: "Your authentication credentials",
  active_campaigns: "Active campaigns you run will stop immediately",
  organized_events_note: "Events you organized may remain visible without your name",
};

const STEP_TITLES = {
  summary: "Delete your account?",
  billing: "Billing and campaigns",
  reauth: "Verify it's you",
  confirm: "Ready to delete",
  done: "Your account has been deleted",
};

export default function DeleteAccountSection({ standalone = false }) {
  const navigate = useNavigate();
  const authInstance = getAuth();
  const user = authInstance.currentUser;
  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("summary");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [reauthLoading, setReauthLoading] = useState(false);
  const [reauthenticated, setReauthenticated] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaSending, setMfaSending] = useState(false);

  const providerType = getProviderType(user);
  const needsBillingStep =
    preview?.subscription?.has_paid_plan ||
    preview?.subscription?.will_be_canceled ||
    (preview?.counts?.active_campaigns || 0) > 0;

  const resetState = () => {
    if (!standalone) {
      setStep("summary");
      setPreview(null);
      setError("");
      setPassword("");
      setReauthenticated(false);
      setMfaResolver(null);
      setMfaVerificationId(null);
      setMfaCode("");
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setStep("summary");
    setError("");
  };

  const handleClose = () => {
    if (deleting) return;
    setOpen(false);
    resetState();
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingPreview(true);
      setError("");
      try {
        const data = await fetchDeletionPreview();
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load deletion details.");
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  const goNextFromSummary = () => {
    setError("");
    setStep(needsBillingStep ? "billing" : "reauth");
  };

  const handleReauth = async () => {
    if (!user) return;
    setReauthLoading(true);
    setError("");
    try {
      await reauthenticateUser(user, { password, authInstance });
      setReauthenticated(true);
      setMfaResolver(null);
      setMfaVerificationId(null);
      setMfaCode("");
      setStep("confirm");
    } catch (err) {
      if (err.code === "auth/multi-factor-auth-required") {
        try {
          setMfaSending(true);
          const challenge = await startMfaReauthChallenge(err, {
            authInstance,
            recaptchaContainerRef,
            recaptchaVerifierRef,
          });
          setMfaResolver(challenge.resolver);
          setMfaVerificationId(challenge.verificationId);
        } catch (mfaErr) {
          setError(mfaErr.message || "Failed to send verification code.");
        } finally {
          setMfaSending(false);
        }
        return;
      }
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Incorrect password. Please try again.");
      } else if (err.code !== "auth/popup-closed-by-user") {
        setError(err.message || "Re-authentication failed.");
      }
    } finally {
      setReauthLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    if (!mfaResolver || !mfaVerificationId || mfaCode.length !== 6) return;
    setReauthLoading(true);
    setError("");
    try {
      await completeMfaReauth(mfaResolver, mfaVerificationId, mfaCode);
      setReauthenticated(true);
      setMfaResolver(null);
      setMfaVerificationId(null);
      setMfaCode("");
      setStep("confirm");
    } catch (err) {
      if (err.code === "auth/invalid-verification-code") {
        setError("Invalid code. Check your SMS and try again.");
      } else {
        setError(err.message || "Verification failed.");
      }
    } finally {
      setReauthLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!reauthenticated) return;
    setDeleting(true);
    setError("");
    try {
      await deleteAccount();
      await signOut(authInstance);
      setStep("done");
      setTimeout(() => navigate("/auth/login"), 1500);
    } catch (err) {
      setError(err.message || "Failed to delete account.");
      setDeleting(false);
    }
  };

  const dialogPaperSx = { borderRadius: "16px", maxWidth: 440, mx: 2 };

  const renderDialogHeader = (title) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: 3,
        pt: 2.5,
        pb: 0.5,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#0f172a", flex: 1, lineHeight: 1.3 }}>
        {title}
      </Typography>
      <IconButton
        onClick={handleClose}
        disabled={deleting}
        aria-label="Close"
        size="small"
        sx={{ color: "#334155" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const renderSummary = () => (
    <>
      <DialogContent sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: "14px", color: "#0f172a", fontWeight: 600, mb: 1.5 }}>
          This will permanently delete:
        </Typography>
        <List dense disablePadding>
          {(preview?.will_delete || []).map((key) => {
            const Icon = SUMMARY_ICONS[key];
            const label = SUMMARY_LABELS[key];
            if (!label) return null;
            return (
              <ListItem key={key} disableGutters sx={{ py: 0.5 }}>
                {Icon ? (
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Icon sx={{ fontSize: 18, color: "#334155" }} />
                  </ListItemIcon>
                ) : null}
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ fontSize: "14px", color: "#0f172a", lineHeight: 1.4 }}
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} variant="outlined" sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button onClick={goNextFromSummary} variant="contained" sx={primaryButtonSx}>
          Continue
        </Button>
      </DialogActions>
    </>
  );

  const renderBilling = () => {
    const sub = preview?.subscription || {};
    return (
      <>
        <DialogContent sx={{ pt: 1 }}>
          {sub.will_be_canceled ? (
            <Typography sx={{ fontSize: "14px", color: "#334155", mb: 1.5 }}>
              Your {sub.display_name} plan will be canceled and you will not be charged again.
            </Typography>
          ) : sub.has_paid_plan ? (
            <Typography sx={{ fontSize: "14px", color: "#334155", mb: 1.5 }}>
              Your personal account will be deleted. {sub.org_account_name || "Your organization's"}{" "}
              subscription is managed separately and will continue.
            </Typography>
          ) : null}
          {sub.other_members_count > 0 ? (
            <Typography sx={{ fontSize: "14px", color: "#334155", mb: 1.5 }}>
              {sub.other_members_count} teammate{sub.other_members_count === 1 ? "" : "s"} on your plan
              will lose paid-plan access immediately.
            </Typography>
          ) : null}
          {(preview?.counts?.active_campaigns || 0) > 0 ? (
            <Typography sx={{ fontSize: "14px", color: "#334155" }}>
              {preview.counts.active_campaigns} active campaign
              {preview.counts.active_campaigns === 1 ? "" : "s"} will stop immediately.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setStep("reauth")} variant="contained" fullWidth sx={primaryButtonSx}>
            Continue
          </Button>
        </DialogActions>
      </>
    );
  };

  const renderReauth = () => (
    <>
      <DialogContent sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: "14px", color: "#334155", mb: 2 }}>
          For your security, confirm with {getProviderLabel(providerType)} before deleting your account.
        </Typography>
        {providerType === "password" && !mfaVerificationId ? (
          <TextField
            fullWidth
            type="password"
            size="small"
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleReauth()}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        ) : null}
        {mfaVerificationId ? (
          <TextField
            fullWidth
            size="small"
            placeholder="000000"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputProps={{
              maxLength: 6,
              style: { fontSize: 24, letterSpacing: 8, textAlign: "center", fontWeight: 700 },
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={mfaVerificationId ? handleMfaVerify : handleReauth}
          variant="contained"
          fullWidth
          disabled={
            reauthLoading ||
            mfaSending ||
            (providerType === "password" && !mfaVerificationId && !password) ||
            (mfaVerificationId && mfaCode.length !== 6)
          }
          sx={primaryButtonSx}
        >
          {reauthLoading || mfaSending ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : mfaVerificationId ? (
            "Verify code"
          ) : (
            `Continue with ${getProviderLabel(providerType)}`
          )}
        </Button>
      </DialogActions>
    </>
  );

  const renderConfirm = () => (
    <>
      <DialogContent sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: "14px", color: "#334155" }}>
          This action is permanent. Your account and associated data will be removed and you will be
          signed out.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={handleDelete}
          disabled={deleting}
          variant="contained"
          color="error"
          fullWidth
          sx={destructiveButtonSx}
        >
          {deleting ? <CircularProgress size={20} color="inherit" /> : "Permanently delete account"}
        </Button>
      </DialogActions>
    </>
  );

  const renderDone = () => (
    <>
      <DialogContent sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: "14px", color: "#334155" }}>
          We&apos;ve signed you out. Redirecting to sign in...
        </Typography>
      </DialogContent>
    </>
  );

  return (
    <>
      {!standalone && (
        <Box sx={{ mt: 3.5, borderTop: "1px solid #e2e8f0", pt: 2 }}>
          <Button
            variant="text"
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 18, color: "#b91c1c" }} />}
            onClick={handleOpen}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#b91c1c",
              justifyContent: "flex-start",
              px: 0.5,
              "&:hover": { bgcolor: "#f8fafc" },
            }}
          >
            Delete account
          </Button>
        </Box>
      )}

      {standalone && !open && (
        <Button
          variant="contained"
          onClick={handleOpen}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#b91c1c",
            "&:hover": { bgcolor: "#991b1b" },
          }}
        >
          Begin account deletion
        </Button>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: dialogPaperSx }}
        fullWidth
        maxWidth="xs"
      >
        {error ? (
          <Box sx={{ px: 3, pt: 1 }}>
            <Alert severity="error" sx={{ fontSize: "13px" }}>
              {error}
            </Alert>
          </Box>
        ) : null}
        {loadingPreview ? (
          <>
            {renderDialogHeader(STEP_TITLES.summary)}
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          </>
        ) : (
          <>
            {renderDialogHeader(STEP_TITLES[step])}
            {step === "summary" && renderSummary()}
            {step === "billing" && renderBilling()}
            {step === "reauth" && renderReauth()}
            {step === "confirm" && renderConfirm()}
            {step === "done" && renderDone()}
          </>
        )}
      </Dialog>

      <div ref={recaptchaContainerRef} />
    </>
  );
}

const cancelButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  color: "#0f172a",
  borderRadius: "10px",
  borderColor: "#0f172a",
};

const primaryButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  bgcolor: "#047857",
  "&:hover": { bgcolor: "#065f46" },
};

const destructiveButtonSx = {
  textTransform: "none",
  fontWeight: 700,
  borderRadius: "10px",
  background: "#b91c1c",
  bgcolor: "#b91c1c",
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    background: "#991b1b",
    bgcolor: "#991b1b",
    boxShadow: "none",
  },
};
